import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from '../axiosConfig';
import Cookies from 'js-cookie';
import axios, { AxiosError } from 'axios';







interface User {
  id: number;
  name: string;
  email: string;
  // Add other user properties as needed
}

interface Organization {
  id: number;
  name: string;
  role: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  registrationError: { [key: string]: string[] } | null;
  organizations: Organization[];
  currentOrganization: Organization | null;
}

const initialState: AuthState = {
  user: null,
  token: null,
  isLoading: false,
  error: null,
  registrationError: null,
  organizations: [],
  currentOrganization: null,
};

interface RegisterUserData {
  name: string;
  email: string;
  password: string;
  auth_provider: string;
  invitation_token?: string;
  referral_code?: string;
}

interface LoginCredentials {
  email: string;
  password: string;
}


interface ResetPasswordData {
  hash_url: string;
  password: string;
  confirm: string;
}

interface GoogleLoginData {
  token: string;
  auth_provider: string;
}

interface SendPasswordUpdateEmailData {
  url: string;
}

interface LoginResponse {
  success: boolean;
  user?: User;
  token?: string;
  message?: string;
  errors?: string;
  hash_url?: string;
}

export const sendPasswordUpdateEmail = createAsyncThunk(
  'auth/sendPasswordUpdateEmail',
  async (data: SendPasswordUpdateEmailData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/user/send-email-to-update-password`,
        data
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

interface RegisterResponse {
  success: boolean;
  status?: number;
  message: {
    registration: string;
    more_information: string;
  };
  hash_url: string;
  user?: User | null;
}

export const registerUser = createAsyncThunk<
  RegisterResponse,
  RegisterUserData,
  { rejectValue: any }
>('auth/register', async (userData, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<RegisterResponse>(
      '/user/register',
      userData
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      return rejectWithValue(error.response.data);
    }
    return rejectWithValue('An unexpected error occurred');
  }
});

export const login = createAsyncThunk<
  LoginResponse,
  LoginCredentials,
  { rejectValue: any }
>('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<LoginResponse>(
      `/user/login`,
      credentials
    );
    const data = response.data;

    if (data.success) {
      // Login successful
      if (data.token) {
        Cookies.set('accessToken', data.token, { expires: 30, secure: false });
        // Update axiosInstance with the new token
        axiosInstance.defaults.headers.common['Authorization'] =
          `Bearer ${data.token}`;
      }
      return data;
    } else {
      // Login failed or additional action required
      return rejectWithValue(data);
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return rejectWithValue(error.response.data);
    }
    return rejectWithValue('An unexpected error occurred');
  }
});

export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async (
    data: { code: string; hash_url: string; purpose: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/user/email/verify`,
        data
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const resendVerification = createAsyncThunk(
  'auth/resendVerification',
  async (data: { hash_url: string }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/user/resend-verification`,
        data
      );
      return response.data; // This should now include hash_url, message, and success
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (data: { email: string }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/user/forgot-password`,
        data
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (
    data: { hash_url: string; password: string; confirm: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/user/reset-password`,
        data
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const googleLogin = createAsyncThunk(
  'auth/googleLogin',
  async (
    googleData: {
      token: string;
      auth_provider: string;
      invitation_token?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/user/google-login`,
        googleData
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const fetchUserProfile = createAsyncThunk(
  'auth/fetchUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      const token = Cookies.get('accessToken');
      const response = await axiosInstance.get('/user/profile');
      return response.data;
    } catch (error) {
      console.error('Error fetching user profile:', error); // Add this line for debugging
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const updateUserName = createAsyncThunk(
  'auth/updateUserName',
  async (newName: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put('/user/name', { name: newName });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);



export const removeCookies = () => {
  Cookies.remove('accessToken', { path: '/' });
  Cookies.remove('currentOrganization', { path: '/' });
}

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await axiosInstance.post('/logout');
      // Remove the token and currentOrganization from cookies
      removeCookies();
      return null;
    } catch (error) {
      // Even if the logout request fails, we still want to clear the cookies
      removeCookies();
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const fetchUserOrganizations = createAsyncThunk(
  'auth/fetchUserOrganizations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/user/organizations');
      return response.data.organizations;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const getInvitationDetails = createAsyncThunk(
  'auth/getInvitationDetails',
  async (token: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/user/invitations/invitation-details?token=${token}`
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
    },
    clearErrors: (state) => {
      state.error = null;
      state.registrationError = null;
    },
    setCurrentOrganization: (state, action: PayloadAction<Organization>) => {
      state.currentOrganization = action.payload;
      Cookies.set('currentOrganization', JSON.stringify(action.payload));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.registrationError = null;
      })
      .addCase(
        registerUser.fulfilled,
        (state, action: PayloadAction<RegisterResponse>) => {
          state.isLoading = false;
          state.user = action.payload.user;
          state.error = null;
          state.registrationError = null;
        }
      )
      .addCase(registerUser.rejected, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        if (typeof action.payload === 'object' && action.payload !== null) {
          state.registrationError = action.payload.error || {
            general: ['Registration failed'],
          };
        } else {
          state.error = action.payload || 'Registration failed';
        }
      })
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.user && action.payload.token) {
          state.user = action.payload.user;
          state.token = action.payload.token;
        }
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        if (typeof action.payload === 'object' && action.payload !== null) {
          state.error =
            action.payload.errors || action.payload.message || 'Login failed';
          // You might want to store hash_url if needed for email verification
          // state.emailVerificationHash = action.payload.hash_url;
        } else {
          state.error = 'An unknown error occurred';
        }
      })
      .addCase(googleLogin.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(googleLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(googleLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) ?? 'An unknown error occurred';
      })
      .addCase(fetchUserProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as string) ?? 'Failed to fetch user profile';
      })
      .addCase(updateUserName.fulfilled, (state, action) => {
        state.user = action.payload.user;
      })
      .addCase(sendPasswordUpdateEmail.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendPasswordUpdateEmail.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(sendPasswordUpdateEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as string) ?? 'Failed to send password update email';
      })
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.currentOrganization = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) ?? 'Logout failed';
      })
      .addCase(
        fetchUserOrganizations.fulfilled,
        (state, action: PayloadAction<Organization[]>) => {
          state.organizations = action.payload;

          const storedOrg = Cookies.get('currentOrganization');
          const mainOrg = action.payload.find((org) => org.role === 'admin');

          state.currentOrganization = storedOrg
            ? JSON.parse(storedOrg)
            : mainOrg || null;

          if (mainOrg && !storedOrg) {
            Cookies.set('currentOrganization', JSON.stringify(mainOrg));
          }
        }
      )
      .addCase(getInvitationDetails.fulfilled, (state, action) => {
        // You can store the invitation details in the state if needed
        // state.invitationDetails = action.payload.invitation;
      })
      .addCase(getInvitationDetails.rejected, (state, action) => {
        state.error =
          (action.payload as string) ?? 'Failed to fetch invitation details';
      });
  },
});

export const { logout, clearErrors, setCurrentOrganization } =
  authSlice.actions;

export default authSlice.reducer;
